import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  CircularProgress,
  IconButton,
  Modal,
  Button
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

function QuotaUsageCard({ companyId }) {
  const { showSuccess, showError } = useToast();
  const [quotaUsages, setQuotaUsages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [quotaToReset, setQuotaToReset] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    fetchQuotaUsages();
  }, [companyId]);

  const fetchQuotaUsages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchCompanyQuotaUsages(companyId);
      
      if (result.success) {
        setQuotaUsages(result.data.quota_usages || []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load quota usage data');
      console.error('Failed to fetch quota usages:', err);
    }
    
    setLoading(false);
  };

  const handleResetClick = (quota) => {
    setQuotaToReset(quota);
    setResetModalOpen(true);
  };

  const handleResetModalClose = () => {
    setResetModalOpen(false);
    setQuotaToReset(null);
  };

  const handleResetConfirm = async () => {
    if (!quotaToReset) return;

    setResetLoading(true);

    try {
      const result = await apiService.resetQuotaUsage(quotaToReset.unique_id);

      if (result.success) {
        console.log('Quota usage reset successfully:', result.data);
        // Update the quota usage in the list
        setQuotaUsages(prev => 
          prev.map(quota => 
            quota.unique_id === quotaToReset.unique_id 
              ? { 
                  ...quota, 
                  used_amount: 0,
                  last_reset: result.data.reset_data.last_reset
                }
              : quota
          )
        );
        setResetModalOpen(false);
        setQuotaToReset(null);
      } else {
        console.error('Failed to reset quota usage:', result.error);
        showError(`Failed to reset quota usage: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error resetting quota usage:', error);
      showError('Failed to reset quota usage. Please try again.', 'Error');
    } finally {
      setResetLoading(false);
    }
  };

  const getUsagePercentage = (used, total) => {
    if (total === 0) return 0;
    return Math.min((used / total) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'success';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat().format(number);
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            📊 Quota Usage
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, flexGrow: 1 }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            📊 Quota Usage
          </Typography>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          📊 Quota Usage
        </Typography>
        
        <Box sx={{ flexGrow: 1 }}>
          {quotaUsages.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              px: 2
            }}>
              <Typography variant="body2" color="text.secondary">
                No quota usage data available
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {quotaUsages.map((usage) => {
                const percentage = getUsagePercentage(usage.used_amount, usage.feature_available_amount);
                const color = getUsageColor(percentage);
                
                return (
                  <Box key={usage.unique_id} sx={{ 
                    p: 3, 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }
                  }}>
                    {/* Header Section */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2.5 
                    }}>
                      <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.1rem' }}>
                        {usage.feature}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Chip 
                          label={`${percentage.toFixed(1)}%`}
                          color={color}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleResetClick(usage)}
                          sx={{ 
                            color: 'primary.main',
                            p: 0.5,
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <Refresh fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {/* Progress Bar Section */}
                    <Box sx={{ mb: 3 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={percentage} 
                        color={color}
                        sx={{ 
                          height: 10, 
                          borderRadius: 5,
                          backgroundColor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 5
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Usage Details Section */}
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                            Used
                          </Typography>
                          <Typography variant="h6" fontWeight="700" color="error.main">
                            {formatNumber(usage.used_amount)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                            Available
                          </Typography>
                          <Typography variant="h6" fontWeight="700" color="success.main">
                            {formatNumber(usage.feature_available_amount)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ 
                          mt: 2, 
                          pt: 2, 
                          borderTop: '1px solid', 
                          borderColor: 'divider' 
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                            Last Reset
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {formatDate(usage.last_reset)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Reset Quota Confirmation Modal */}
      <Modal
        open={resetModalOpen}
        onClose={handleResetModalClose}
        aria-labelledby="reset-quota-modal"
        aria-describedby="reset-quota-confirmation"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 450,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom color="warning.main" sx={{ mb: 3, fontWeight: 600 }}>
            Reset Quota Usage
          </Typography>
          
          {quotaToReset && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                Are you sure you want to reset the quota usage for:
              </Typography>
              <Box sx={{ 
                p: 3, 
                bgcolor: 'warning.light', 
                borderRadius: 2, 
                border: '1px solid',
                borderColor: 'warning.main',
                mb: 3
              }}>
                <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
                  {quotaToReset.feature}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Current Usage: {formatNumber(quotaToReset.used_amount)} / {formatNumber(quotaToReset.feature_available_amount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last Reset: {formatDate(quotaToReset.last_reset)}
                </Typography>
              </Box>
              <Typography variant="body2" color="warning.main" sx={{ mb: 3, fontWeight: 500 }}>
                This will set the used amount to 0 and update the last reset time.
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button 
              variant="outlined" 
              onClick={handleResetModalClose}
              disabled={resetLoading}
              sx={{ px: 3, py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="warning"
              onClick={handleResetConfirm}
              disabled={resetLoading}
              sx={{ px: 3, py: 1, minWidth: 100 }}
            >
              {resetLoading ? 'Resetting...' : 'Reset'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Card>
  );
}

export default QuotaUsageCard; 